/**
 * Notification Services Orchestrator
 * این فایل همه سرویس‌های نوتیفیکیشن را مدیریت و export می‌کند
 */

import { welcomeService } from './welcomeService';
import { analysisService } from './analysisService';
import { reminderService } from './reminderService';
import { pushService } from '../push/pushService';
import { subscriptionManager } from '../push/subscriptionManager';

/**
 * Notification Manager - مدیر اصلی همه نوتیفیکیشن‌ها
 * این کلاس همه سرویس‌ها را ترکیب می‌کند و API واحد فراهم می‌کند
 */
class NotificationManager {
  constructor() {
    this.welcome = welcomeService;
    this.analysis = analysisService;
    this.reminder = reminderService;
    this.push = pushService;
    this.subscription = subscriptionManager;
    this.initialized = false;
  }

  /**
   * Initialize notification system
   * راه‌اندازی سیستم نوتیفیکیشن
   * 
   * @returns {Promise<boolean>}
   */
  async initialize() {
    try {
      if (this.initialized) {
        console.log('✅ Notification system already initialized');
        return true;
      }

      console.log('🔔 Initializing notification system...');

      // Initialize push service
      const pushInitialized = await this.push.initialize();
      if (!pushInitialized) {
        console.warn('⚠️ Push service initialization failed');
        return false;
      }

      // Initialize subscription manager
      const subscriptionInitialized = await this.subscription.initialize();
      if (!subscriptionInitialized) {
        console.warn('⚠️ Subscription manager initialization failed');
        return false;
      }

      this.initialized = true;
      console.log('✅ Notification system initialized successfully');
      
      return true;
    } catch (error) {
      console.error('❌ Failed to initialize notification system:', error);
      return false;
    }
  }

  /**
   * Setup notifications for first-time user
   * راه‌اندازی نوتیفیکیشن‌ها برای کاربر جدید
   * 
   * @param {string} userName - نام کاربر
   * @param {string} userId - شناسه کاربر
   * @param {Object} options - تنظیمات اضافی
   * @returns {Promise<Object>} - نتیجه راه‌اندازی
   */
  async setupForNewUser(userName, userId, options = {}) {
    try {
      console.log('🎉 Setting up notifications for new user...');

      // Initialize system
      await this.initialize();

      const result = {
        permission: false,
        subscription: false,
        welcome: false,
        analysis: false,
      };

      // Request permission
      const permission = await this.push.requestPermission();
      result.permission = permission === 'granted';

      if (!result.permission) {
        console.warn('⚠️ Notification permission not granted');
        return result;
      }

      // Subscribe to push notifications
      if (options.subscribeToPush !== false) {
        result.subscription = await this.push.subscribe(userId);
      }

      // Send or schedule welcome notification
      if (options.welcomeDelay > 0) {
        await this.welcome.schedule(userName, options.welcomeDelay, userId);
        result.welcome = true;
      } else if (options.welcomeImmediate) {
        result.welcome = await this.welcome.sendImmediate(userName, userId);
      }

      // Schedule analysis reminder (default: 7 days)
      if (options.analysisReminderDelay !== false) {
        const delay = options.analysisReminderDelay || 7 * 24 * 60 * 60 * 1000;
        await this.analysis.scheduleReminder(delay, userId);
        result.analysis = true;
      }

      console.log('✅ Notifications setup complete:', result);
      return result;
    } catch (error) {
      console.error('❌ Failed to setup notifications for new user:', error);
      return { permission: false, subscription: false, welcome: false, analysis: false };
    }
  }

  /**
   * Setup routine reminders
   * راه‌اندازی یادآوری‌های روتین
   * 
   * @param {Object} times - زمان‌های روتین
   * @param {string} userId - شناسه کاربر
   * @returns {Promise<Array>} - آرایه شناسه‌های notification
   */
  async setupRoutineReminders(times, userId) {
    try {
      console.log('⏰ Setting up routine reminders...');
      
      // Initialize system
      await this.initialize();

      // Check permission
      if (Notification.permission !== 'granted') {
        console.warn('⚠️ Notification permission not granted');
        return [];
      }

      // Schedule daily reminders
      const notifications = await this.reminder.scheduleDailyRoutine(times, userId);
      
      console.log(`✅ Setup ${notifications.length} routine reminders`);
      return notifications;
    } catch (error) {
      console.error('❌ Failed to setup routine reminders:', error);
      return [];
    }
  }

  /**
   * Send analysis complete notification
   * ارسال نوتیفیکیشن اتمام تحلیل
   * 
   * @param {Object} analysisData - داده‌های تحلیل
   * @param {string} userId - شناسه کاربر
   * @returns {Promise<boolean>}
   */
  async notifyAnalysisComplete(analysisData, userId) {
    try {
      console.log('📊 Sending analysis complete notification...');
      
      // Initialize system
      await this.initialize();

      // Check permission
      if (Notification.permission !== 'granted') {
        console.warn('⚠️ Notification permission not granted');
        return false;
      }

      // Send notification
      const result = await this.analysis.sendComplete(analysisData, userId);
      
      if (result) {
        console.log('✅ Analysis complete notification sent');
      }
      
      return result;
    } catch (error) {
      console.error('❌ Failed to send analysis complete notification:', error);
      return false;
    }
  }

  /**
   * Cancel all notifications
   * لغو همه نوتیفیکیشن‌ها
   * 
   * @returns {Object} - تعداد نوتیفیکیشن‌های لغو شده
   */
  cancelAll() {
    try {
      console.log('🔕 Cancelling all notifications...');
      
      const result = {
        welcome: this.welcome.cancelAll(),
        analysis: this.analysis.cancelAll(),
        reminder: this.reminder.cancelAll(),
      };
      
      const total = result.welcome + result.analysis + result.reminder;
      console.log(`✅ Cancelled ${total} notifications`);
      
      return result;
    } catch (error) {
      console.error('❌ Failed to cancel all notifications:', error);
      return { welcome: 0, analysis: 0, reminder: 0 };
    }
  }

  /**
   * Test notification system
   * تست سیستم نوتیفیکیشن
   * 
   * @returns {Promise<boolean>}
   */
  async test() {
    try {
      console.log('🧪 Testing notification system...');
      
      // Initialize system
      await this.initialize();

      // Test notification
      const result = await this.push.testNotification();
      
      if (result) {
        console.log('✅ Notification system test passed');
      } else {
        console.log('❌ Notification system test failed');
      }
      
      return result;
    } catch (error) {
      console.error('❌ Notification system test error:', error);
      return false;
    }
  }

  /**
   * Get notification status
   * دریافت وضعیت نوتیفیکیشن
   * 
   * @returns {Promise<Object>}
   */
  async getStatus() {
    try {
      const status = {
        supported: 'Notification' in window && 'serviceWorker' in navigator,
        permission: Notification.permission,
        subscribed: await this.subscription.isSubscribed(),
        subscription: await this.subscription.getSubscription(),
      };
      
      console.log('📊 Notification status:', status);
      return status;
    } catch (error) {
      console.error('❌ Failed to get notification status:', error);
      return { supported: false, permission: 'denied', subscribed: false, subscription: null };
    }
  }
}

// Export singleton instance
export const notificationManager = new NotificationManager();

// Export individual services for direct access
export {
  welcomeService,
  analysisService,
  reminderService,
  pushService,
  subscriptionManager,
};

// Export as default
export default notificationManager;



